import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  StatusBadge,
  Textarea,
  cn,
} from "../../shared/ui";
import type { ProjectDetail, ProjectHealth } from "../../shared/types/api";
import { HEALTH_TONE } from "./projectVocabulary";

const CHOICES: { value: ProjectHealth | "DERIVED"; label: string; help: string }[] = [
  {
    value: "DERIVED",
    label: "Let the numbers decide",
    help: "Health follows effort and schedule variance, and updates itself as they move.",
  },
  {
    value: "GREEN",
    label: "Green",
    help: "On track, whatever the variance currently reads.",
  },
  {
    value: "AMBER",
    label: "Amber",
    help: "At risk — a concern the numbers do not yet show.",
  },
  {
    value: "RED",
    label: "Red",
    help: "In trouble, and it needs attention now.",
  },
];

/**
 * Sets or clears a manual health rating (§30).
 *
 * <p>Overriding is legitimate: a manager often knows a risk before it reaches the numbers. What is
 * not legitimate is an unexplained colour, so a reason is required — and the derived value stays
 * visible beside it, so nobody can quietly paint a red project green and have it read as fact.
 */
export function HealthOverrideDrawer({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [choice, setChoice] = useState<ProjectHealth | "DERIVED">("DERIVED");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setChoice(project.healthOverride ?? "DERIVED");
      setReason(project.healthOverrideReason ?? "");
      setError(null);
    }
  }, [project]);

  const submit = async () => {
    if (!project) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${project.id}/health`, {
        health: choice === "DERIVED" ? null : choice,
        reason: choice === "DERIVED" ? null : reason,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the health.");
    } finally {
      setSaving(false);
    }
  };

  const needsReason = choice !== "DERIVED" && reason.trim() === "";

  return (
    <Drawer
      open={Boolean(project)}
      onClose={onClose}
      title="Project health"
      subtitle={project?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={needsReason}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {project && (
          <div className="flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5">
            <span className="text-sm text-ink-500">The variance suggests</span>
            <StatusBadge tone={HEALTH_TONE[project.forecast.impliedHealth]}>
              {project.forecast.impliedHealth}
            </StatusBadge>
          </div>
        )}

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium text-ink-700">Show this project as</legend>
          {CHOICES.map((option) => (
            <label
              key={option.value}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                choice === option.value
                  ? "border-brand-400 bg-brand-50"
                  : "border-ink-200 hover:border-ink-300",
              )}
            >
              <input
                type="radio"
                name="health"
                className="mt-1 accent-brand-600"
                value={option.value}
                checked={choice === option.value}
                onChange={() => setChoice(option.value)}
              />
              <span>
                <span className="block text-sm font-medium text-ink-900">{option.label}</span>
                <span className="mt-0.5 block text-sm text-ink-500">{option.help}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {choice !== "DERIVED" && (
          <Field
            label="Why"
            required
            hint="Shown wherever this project's health appears, so the colour can be read in context."
          >
            <Textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Key integration partner has slipped their delivery date by three weeks."
            />
          </Field>
        )}

        {choice === "DERIVED" && project?.healthOverride && (
          <InlineAlert tone="info">
            Saving this removes the manual rating. Health goes back to following the variance, and
            will read {project.forecast.impliedHealth.toLowerCase()} straight away.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
